import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectVendorsAPI, projectResponsesAPI } from '../../services/api';
import { formatDateTime } from '../../utils/formHelpers';

/**
 * 回覆追蹤 Tab - 排行榜形式
 * 點擊供應商可展開查看所有問卷狀態
 */
export default function ProjectResponsesTab({ projectId }) {
  const [rankType, setRankType] = useState('completion'); // completion, speed, pending
  const [expandedVendor, setExpandedVendor] = useState(null); // 展開的供應商 ID
  // 已展開歷史版本的問卷（可同時展開多份）：`${vendorId}_${formId}`
  const [expandedForms, setExpandedForms] = useState(new Set());
  const toggleForm = (key) => {
    setExpandedForms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // 取得供應商及其指派資料
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', projectId, 'vendors-with-assignments'],
    queryFn: () => projectVendorsAPI.getWithAssignments(projectId),
    staleTime: 60 * 1000,
  });

  const vendors = data?.vendors || [];

  // 計算統計和排行
  const { rankings, stats } = useMemo(() => {
    if (!vendors.length) return { rankings: [], stats: {} };

    // 計算每個供應商的統計
    const vendorStats = vendors.map(vendor => {
      const assignments = vendor.form_assignments || [];
      const completed = assignments.filter(a => a.fill_status === 'completed').length;
      const pending = assignments.filter(a => a.fill_status === 'pending').length;
      const sent = assignments.filter(a => a.fill_status === 'sent').length;
      const total = assignments.length;

      return {
        ...vendor,
        completed,
        pending,
        sent,
        total,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      };
    });

    // 總體統計
    const totalVendors = vendorStats.length;
    const fullyCompleted = vendorStats.filter(v => v.completionRate === 100).length;
    const partiallyCompleted = vendorStats.filter(v => v.completionRate > 0 && v.completionRate < 100).length;
    const notStarted = vendorStats.filter(v => v.completionRate === 0 && v.total > 0).length;
    const noAssignment = vendorStats.filter(v => v.total === 0).length;
    const avgCompletion = totalVendors > 0
      ? Math.round(vendorStats.reduce((sum, v) => sum + v.completionRate, 0) / totalVendors)
      : 0;

    // 排序
    let sorted = [...vendorStats];
    if (rankType === 'completion') {
      sorted.sort((a, b) => {
        if (b.completionRate !== a.completionRate) return b.completionRate - a.completionRate;
        return b.completed - a.completed;
      });
    } else if (rankType === 'speed') {
      sorted.sort((a, b) => b.completed - a.completed);
    } else if (rankType === 'pending') {
      sorted.sort((a, b) => b.pending - a.pending);
    }

    return {
      rankings: sorted,
      stats: {
        totalVendors,
        fullyCompleted,
        partiallyCompleted,
        notStarted,
        noAssignment,
        avgCompletion,
      }
    };
  }, [vendors, rankType]);

  const totalPages = Math.ceil(rankings.length / pageSize);
  const paginatedRankings = rankings.slice((page - 1) * pageSize, page * pageSize);

  // 排名徽章顏色
  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-yellow-400 text-yellow-900';
    if (rank === 2) return 'bg-gray-300 text-gray-700';
    if (rank === 3) return 'bg-amber-600 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  // 完成度顏色
  const getCompletionColor = (rate) => {
    if (rate === 100) return 'text-green-600';
    if (rate >= 50) return 'text-yellow-600';
    if (rate > 0) return 'text-orange-600';
    return 'text-gray-400';
  };

  const getCompletionBg = (rate) => {
    if (rate === 100) return 'bg-green-100';
    if (rate >= 50) return 'bg-yellow-100';
    if (rate > 0) return 'bg-orange-100';
    return 'bg-gray-100';
  };

  // 問卷狀態顯示
  const getFormStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">已完成</span>;
      case 'pending':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">待填寫</span>;
      case 'sent':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">已發送</span>;
      default:
        return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">未知</span>;
    }
  };

  // 切換展開
  const toggleExpand = (vendorId) => {
    setExpandedVendor(expandedVendor === vendorId ? null : vendorId);
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">載入失敗: {error.message}</p>
      </div>
    );
  }

  const rankTypes = [
    { value: 'completion', label: '完成度', icon: '🏆' },
    { value: 'speed', label: '已完成數', icon: '⚡' },
    { value: 'pending', label: '待處理', icon: '⏳' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* 統計卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.totalVendors}</div>
          <div className="text-sm text-purple-800">供應商總數</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.fullyCompleted}</div>
          <div className="text-sm text-green-800">全部完成</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.partiallyCompleted}</div>
          <div className="text-sm text-yellow-800">部分完成</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.notStarted}</div>
          <div className="text-sm text-orange-800">尚未開始</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-600">{stats.avgCompletion}%</div>
          <div className="text-sm text-gray-800">平均完成率</div>
        </div>
      </div>

      {/* 排行榜 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* 標題列 */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-xl">🏆</span>
              供應商填答排行
            </h3>
            <div className="flex gap-1">
              {rankTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setRankType(type.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1 ${
                    rankType === type.value
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>{type.icon}</span>
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 排行內容 */}
        <div className="divide-y divide-gray-100">
          {rankings.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-5xl mb-4">📊</div>
              <p className="text-gray-500">尚無供應商資料</p>
            </div>
          ) : (
            paginatedRankings.map((vendor, pIdx) => {
              const index = (page - 1) * pageSize + pIdx;
              return (
              <div key={vendor.id}>
                {/* 供應商行 - 可點擊展開 */}
                <div
                  onClick={() => { toggleExpand(vendor.id); setExpandedForms(new Set()); }}
                  className={`flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition cursor-pointer ${
                    expandedVendor === vendor.id ? 'bg-purple-50' : ''
                  }`}
                >
                  {/* 排名 */}
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${getRankBadge(index + 1)}`}
                  >
                    {index + 1}
                  </div>

                  {/* 供應商資訊 */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {vendor.name}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span>{vendor.contact_email || '-'}</span>
                      {vendor.tags?.length > 0 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">
                          {vendor.tags[0]}
                          {vendor.tags.length > 1 && ` +${vendor.tags.length - 1}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 進度條 */}
                  <div className="w-32 hidden sm:block">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          vendor.completionRate === 100 ? 'bg-green-500' :
                          vendor.completionRate >= 50 ? 'bg-yellow-500' :
                          vendor.completionRate > 0 ? 'bg-orange-500' : 'bg-gray-300'
                        }`}
                        style={{ width: `${vendor.completionRate}%` }}
                      />
                    </div>
                  </div>

                  {/* 統計數字 */}
                  <div className="text-right flex-shrink-0">
                    <div className={`font-bold ${getCompletionColor(vendor.completionRate)}`}>
                      {vendor.completionRate}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {vendor.completed}/{vendor.total} 份
                    </div>
                  </div>

                  {/* 狀態細節 */}
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0 ${getCompletionBg(vendor.completionRate)}`}>
                    {vendor.completionRate === 100 ? (
                      <span className="text-green-700">✓ 全部完成</span>
                    ) : vendor.pending > 0 ? (
                      <span className="text-orange-700">待處理 {vendor.pending}</span>
                    ) : vendor.sent > 0 ? (
                      <span className="text-blue-700">已發送 {vendor.sent}</span>
                    ) : (
                      <span className="text-gray-500">未指派</span>
                    )}
                  </div>

                  {/* 展開箭頭 */}
                  <div className="flex-shrink-0">
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedVendor === vendor.id ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* 展開的問卷列表 */}
                {expandedVendor === vendor.id && (
                  <div className="bg-gray-50 border-t border-gray-100">
                    {vendor.form_assignments && vendor.form_assignments.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {vendor.form_assignments.map((assignment, idx) => {
                          const formKey = `${vendor.id}_${assignment.form_id}`;
                          const hasVersions = (assignment.version_count || 0) > 0;
                          const isFormOpen = expandedForms.has(formKey);
                          return (
                          <div key={assignment.form_id || idx}>
                          <div
                            onClick={() => hasVersions && toggleForm(formKey)}
                            className={`px-6 py-3 pl-14 flex items-center justify-between ${
                              hasVersions ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'
                            }`}
                            title={hasVersions ? '點擊查看歷史版本' : '尚無回覆紀錄'}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`text-xs w-4 ${hasVersions ? 'text-purple-600' : 'text-gray-300'}`}>
                                {isFormOpen ? '▼' : '▶'}
                              </span>
                              <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                              <div>
                                <div className="font-medium text-gray-800">
                                  {assignment.form_name || '未命名問卷'}
                                </div>
                                {assignment.deadline && (
                                  <div className="text-xs text-gray-500">
                                    截止日：{new Date(assignment.deadline).toLocaleDateString('zh-TW')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {assignment.submitted_at && (
                                <span className="text-xs text-gray-400">
                                  {new Date(assignment.submitted_at).toLocaleDateString('zh-TW')} 提交
                                </span>
                              )}
                              {getFormStatusBadge(assignment.fill_status)}
                            </div>
                          </div>
                          {isFormOpen && (
                            <FormVersionList
                              projectId={projectId}
                              vendorId={vendor.id}
                              vendorName={vendor.name}
                              formId={assignment.form_id}
                              formName={assignment.form_name}
                            />
                          )}
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-6 py-4 pl-20 text-gray-400 text-sm">
                        尚未指派問卷
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>

        {/* 分頁控制 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <div className="text-sm text-gray-500">
              顯示 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, rankings.length)} / {rankings.length} 家
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                上一頁
              </button>
              <span className="px-2 py-1 text-sm text-gray-600">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                下一頁
              </button>
            </div>
          </div>
        )}

        {/* 底部統計 */}
        {rankings.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
            <span className="text-xs text-gray-500">
              共 {stats.totalVendors} 家供應商，{stats.fullyCompleted} 家已全部完成（點擊供應商查看問卷明細）
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// 每次提交本來就不同的欄位（提交時間、驗證碼），比較時不標黃底
const META_KEYS = new Set(['時間戳記', 'Timestamp', 'タイムスタンプ', '驗證碼']);

const SOURCE_LABELS = {
  google_form: 'Google 表單',
  excel: 'Excel 上傳',
  manual: '人工輸入',
};

/**
 * 版本標籤：目前計分用的有效版／重填前的舊版本
 */
function VersionBadge({ version }) {
  if (version.is_current) {
    return <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[11px]">有效（計分）</span>;
  }
  if (version.status === 'superseded') {
    return <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[11px]">舊版本</span>;
  }
  return <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[11px]">未採用</span>;
}

/**
 * 第二層展開：某廠商某問卷的歷史版本清單（新到舊）
 */
function FormVersionList({ projectId, vendorId, vendorName, formId, formName }) {
  const [showCompare, setShowCompare] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', projectId, 'response-versions', vendorId, formId],
    queryFn: () => projectResponsesAPI.getVersions(projectId, vendorId, formId),
    staleTime: 60 * 1000,
  });
  const versions = data?.versions || [];

  if (isLoading) {
    return <div className="pl-24 pr-6 pb-3 text-xs text-gray-400">載入歷史版本…</div>;
  }
  if (error) {
    return <div className="pl-24 pr-6 pb-3 text-xs text-red-600">載入失敗：{error.message}</div>;
  }
  if (versions.length === 0) {
    return <div className="pl-24 pr-6 pb-3 text-xs text-gray-400">尚無回覆紀錄</div>;
  }

  return (
    <div className="pl-24 pr-6 pb-3">
      <div className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
        {[...versions].reverse().map((v) => (
          <div key={v.id} className={`px-3 py-2 flex items-center gap-3 text-xs ${v.is_current ? '' : 'text-gray-500'}`}>
            <span className="font-semibold text-gray-700 w-6">v{v.version}</span>
            <VersionBadge version={v} />
            <span className="text-gray-600">{formatDateTime(v.submitted_at)}</span>
            <span className="text-gray-400">{SOURCE_LABELS[v.source] || v.source}</span>
          </div>
        ))}
        <div className="px-3 py-2 flex justify-end">
          <button
            onClick={() => setShowCompare(true)}
            className="px-3 py-1 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition"
          >
            {versions.length > 1 ? '比較答案' : '查看答案'}
          </button>
        </div>
      </div>
      {showCompare && (
        <VersionCompareModal
          vendorName={vendorName}
          formName={formName}
          versions={versions}
          headers={data?.response_headers || []}
          onClose={() => setShowCompare(false)}
        />
      )}
    </div>
  );
}

/**
 * 比較答案視窗：每個版本一欄（新到舊）、每題一列，與前一版不同的答案以黃底標示（唯讀）
 */
function VersionCompareModal({ vendorName, formName, versions, headers, onClose }) {
  const newestFirst = [...versions].reverse();
  const current = versions.find((v) => v.is_current);

  // 題目順序：優先依 Google Sheet 欄位順序，其餘欄位接在後面
  const allKeys = new Set();
  versions.forEach((v) => Object.keys(v.response_data || {}).forEach((k) => allKeys.add(k)));
  const questions = [
    ...headers.filter((h) => allKeys.has(h)),
    ...[...allKeys].filter((k) => !headers.includes(k)),
  ];

  const valueOf = (v, q) => {
    const val = v.response_data?.[q];
    if (val === undefined || val === null || val === '') return '';
    return typeof val === 'object' ? JSON.stringify(val) : String(val);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{versions.length > 1 ? '比較答案' : '查看答案'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{vendorName}</p>
          <p className="text-xs text-gray-600">{formName}</p>
          <p className="text-xs text-purple-600 mt-1">
            共 {versions.length} 個版本{current ? `，計分採用 v${current.version}` : '，目前沒有有效版本（等待廠商重填）'}
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-auto min-h-0">
          <table className="w-full border-collapse">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-300 min-w-[240px] sticky left-0 bg-gray-100">題目</th>
                {newestFirst.map((v) => (
                  <th key={v.id} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-300 min-w-[180px]">
                    <div className="flex items-center gap-1">v{v.version} <VersionBadge version={v} /></div>
                    <div className="font-normal text-gray-500 mt-0.5">
                      {formatDateTime(v.submitted_at)} · {SOURCE_LABELS[v.source] || v.source}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {questions.map((q, qi) => (
                <tr key={q} className={`border-b border-gray-100 ${qi % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="px-3 py-2 text-xs text-gray-700 border-r border-gray-100 sticky left-0 bg-inherit">{q}</td>
                  {newestFirst.map((v, i) => {
                    const older = newestFirst[i + 1];
                    const changed = !!older && !META_KEYS.has(q) && valueOf(v, q) !== valueOf(older, q);
                    return (
                      <td
                        key={v.id}
                        className={`px-3 py-2 text-xs border-r border-gray-100 whitespace-pre-wrap break-words ${
                          changed ? 'bg-yellow-50 font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {valueOf(v, q) || <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {versions.length > 1 && (
              <>
                <span className="inline-block w-3 h-3 bg-yellow-50 border border-yellow-200 align-middle mr-1" />
                黃底＝與前一版不同的答案 ·{' '}
              </>
            )}
            歷史版本僅供查看，不能修改
          </p>
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}
